/**
 * Notification Context
 *
 * React context for managing notifications across the application.
 *
 * @since 1.0.0
 */

import { createContext, useCallback, useState } from "react";

import type {
  Notification,
  NotificationContextValue,
  NotificationType,
} from "./types";

/**
 * Notification context
 *
 * @since 1.0.0
 */
export const NotificationContext = createContext<
  NotificationContextValue | undefined
>(undefined);

/**
 * Notification context provider props
 *
 * @since 1.0.0
 */
export interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * Notification context provider component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const NotificationProvider = ({
  children,
}: NotificationProviderProps): JSX.Element => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  /**
   * Remove a notification by ID
   *
   * @since 1.0.0
   *
   * @param id Notification ID to remove.
   *
   * @return void
   */
  const removeNotification = useCallback((id: string): void => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  }, []);

  /**
   * Add a notification
   *
   * @since 1.0.0
   *
   * @param type Notification type.
   * @param message Notification message.
   * @param options Optional notification options.
   *
   * @return Notification ID.
   */
  const addNotification = useCallback(
    (
      type: NotificationType,
      message: string,
      options?: {
        dismissible?: boolean;
        autoDismiss?: boolean;
        duration?: number;
      },
    ): string => {
      const id = `notification-${Date.now()}-${Math.random()}`;
      const notification: Notification = {
        id,
        variant: type,
        message,
        dismissible: options?.dismissible ?? true,
        autoDismiss: options?.autoDismiss ?? true,
        duration: options?.duration ?? 5000,
      };

      setNotifications((prev) => [...prev, notification]);

      // Auto-dismiss if enabled
      if (notification.autoDismiss) {
        setTimeout(() => {
          removeNotification(id);
        }, notification.duration);
      }

      return id;
    },
    [removeNotification],
  );

  /**
   * Clear all notifications
   *
   * @since 1.0.0
   *
   * @return void
   */
  const clearNotifications = useCallback((): void => {
    setNotifications([]);
  }, []);

  const value: NotificationContextValue = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
